/**
 * studentOfferAdapter — la carte d'offre de l'élève (`student/account/subscription/BecomePremiumCard.js`),
 * montrée par « Messages » et au-dessus des « Activités faites », et la lecture des droits des
 * rôles qu'elle et d'autres écrans de l'élève demandent (`ability.can("read", …)`).
 */
import { Ability } from "@casl/ability"
import moment from "moment"

import { abilitiesForRoles, getPrivilegedRole } from "../../../../../data/utility/ability"
import { SUBSCRIBED_STATUSES, SUBSCRIPTION_PAUSED_STATUSES } from "../../parent/Subscription/subscriptionAdapter"
import { type UIPausePeriod } from "../../../ui/types/studentTutoring"

/** Forme lâche : le compte vient d'un réducteur en JS, tous les champs sont optionnels. */
export type ApiStudentOfferAccount = {
    roles?: string[] | null
    subscription_state?: {
        status?: string | null
        end_date?: string | null
        planned_start_date?: string | null
    } | null
}

/** `ability.can("read", subject)` — l'ability de l'app est celle des rôles du compte (`App.js`). */
export function canReadSubject(roles: string[] | null | undefined, subject: string): boolean {
    return new Ability(abilitiesForRoles(roles ?? null)).can("read", subject)
}

/** La carte de l'offre : l'essai à démarrer, ou la pause qu'elle dit à sa place. */
export type StudentOffer = { kind: "trial" } | { kind: "paused"; pausePeriod?: UIPausePeriod }

/**
 * La carte de `BecomePremiumCard.js`, sous sa condition d'affichage (`:43`, `:70-72`) : un élève,
 * sans abonnement, hors stage (`holiday_course_views`), sans formule (`getPrivilegedRole` →
 * `basic` ; `canSubscribeToEssential` y ajoute « sans tuteur », que `canSubscribeToTrialEssential`
 * couvre déjà). Abonnement en pause : la carte dit la pause et sa période — seulement quand la
 * reprise suit le début (`subscriptionPausePeriod`, `dataUtils.js`), sinon la phrase s'arrête.
 * Sinon : l'essai (`start-trial`, le seul bouton atteint).
 */
export function studentOffer(user: ApiStudentOfferAccount | null | undefined): StudentOffer | null {
    if (!user?.roles?.includes("student")) return null
    const status = user.subscription_state?.status ?? ""
    if (SUBSCRIBED_STATUSES.includes(status)) return null
    if (canReadSubject(user.roles, "holiday_course_views")) return null
    if (getPrivilegedRole(user) !== "basic") return null

    if (!SUBSCRIPTION_PAUSED_STATUSES.includes(status)) return { kind: "trial" }

    const start = user.subscription_state?.end_date
    const end = user.subscription_state?.planned_start_date
    const ordered = start != null && end != null && moment(start).isBefore(end)
    return ordered ? { kind: "paused", pausePeriod: { start, end } } : { kind: "paused" }
}
